/**
 * Collectibles: Buzz propellers accelerate then spin when in view;
 * Woody cape cycles 3 frames when in view. Pauses off-screen / hidden tab.
 * Uses shared window.__mt5bwScheduleSTRefresh when available.
 */
(function () {
  "use strict";

  const ROOT_SELECTOR = "[data-mt5bw-collectibles]";
  const BUZZ_SELECTOR = "[data-mt5bw-collectibles-buzz]";
  const ROTOR_SELECTOR = "[data-mt5bw-collectibles-rotor]";
  const WOODY_SELECTOR = "[data-mt5bw-collectibles-woody]";
  const WOODY_FRAME_SELECTOR = "[data-mt5bw-woody-frame]";
  const SHARED_REFRESH_KEY = "__mt5bwScheduleSTRefresh";

  const ACCEL_DURATION = 2.4;
  const STEADY_DURATION = 0.65;
  const ACCEL_EASE = "power2.in";
  const WOODY_FRAME_INTERVAL_MS = 680;
  /** Bounce sequence: 1 → 2 → 3 → 2 → (repeat) */
  const WOODY_FRAME_SEQUENCE = [0, 1, 2, 1];

  /**
   * @type {WeakMap<Element, {
   *   trigger?: ScrollTrigger,
   *   woodyTrigger?: ScrollTrigger,
   *   frameTimer?: number,
   *   woodyStep?: number,
   *   rotors?: Element[],
   *   woodyFrames?: Element[],
   *   buzzInView?: boolean,
   *   woodyInView?: boolean,
   *   rotorStarted?: boolean,
   *   steadyTween?: gsap.core.Tween,
   *   accelTween?: gsap.core.Tween,
   *   tweens: gsap.core.Tween[]
   * }>}
   */
  const stateMap = new WeakMap();
  /** @type {Set<Element>} */
  const activeRoots = new Set();
  let visibilityBound = false;

  function prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function refreshTriggers(force) {
    if (typeof window[SHARED_REFRESH_KEY] === "function") {
      window[SHARED_REFRESH_KEY](force);
      return;
    }

    if (typeof ScrollTrigger === "undefined" || ScrollTrigger.isRefreshing) {
      return;
    }

    ScrollTrigger.refresh();
  }

  function resetWoodyFrames(frames, activeIndex) {
    const index = typeof activeIndex === "number" ? activeIndex : 0;
    frames.forEach((frame, i) => {
      frame.classList.toggle("is-active", i === index);
    });
  }

  function pauseRotor(state) {
    if (state.accelTween && state.accelTween.isActive()) {
      state.accelTween.pause();
    }
    if (state.steadyTween && state.steadyTween.isActive()) {
      state.steadyTween.pause();
    }
  }

  function resumeRotor(state, rotors) {
    if (!rotors.length || typeof gsap === "undefined") {
      return;
    }

    if (state.steadyTween) {
      state.steadyTween.resume();
      return;
    }

    if (state.accelTween) {
      state.accelTween.resume();
      return;
    }

    startRotorSpin(rotors, state);
  }

  function stopWoodyFrameCycle(state) {
    if (state.frameTimer) {
      clearInterval(state.frameTimer);
      state.frameTimer = undefined;
    }
  }

  function startWoodyFrameCycle(frames, state) {
    if (!frames.length || state.frameTimer) {
      return;
    }

    if (typeof state.woodyStep !== "number") {
      state.woodyStep = 0;
      resetWoodyFrames(frames, WOODY_FRAME_SEQUENCE[0]);
    }

    state.frameTimer = window.setInterval(() => {
      state.woodyStep = (state.woodyStep + 1) % WOODY_FRAME_SEQUENCE.length;
      const activeIndex = WOODY_FRAME_SEQUENCE[state.woodyStep];
      resetWoodyFrames(frames, activeIndex);
    }, WOODY_FRAME_INTERVAL_MS);
  }

  function pauseAllAnimations() {
    activeRoots.forEach((root) => {
      const state = stateMap.get(root);
      if (!state) {
        return;
      }
      pauseRotor(state);
      stopWoodyFrameCycle(state);
    });
  }

  function resumeInViewAnimations() {
    if (document.hidden || prefersReducedMotion()) {
      return;
    }

    activeRoots.forEach((root) => {
      const state = stateMap.get(root);
      if (!state) {
        return;
      }

      if (state.buzzInView && state.rotors) {
        resumeRotor(state, state.rotors);
      }

      if (state.woodyInView && state.woodyFrames) {
        startWoodyFrameCycle(state.woodyFrames, state);
      }
    });
  }

  function bindVisibilityListener() {
    if (visibilityBound) {
      return;
    }
    visibilityBound = true;

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        pauseAllAnimations();
      } else {
        resumeInViewAnimations();
      }
    });
  }

  function destroyRoot(root) {
    const state = stateMap.get(root);
    if (!state) {
      return;
    }

    activeRoots.delete(root);

    if (state.trigger) {
      state.trigger.kill();
      state.trigger = undefined;
    }

    if (state.woodyTrigger) {
      state.woodyTrigger.kill();
      state.woodyTrigger = undefined;
    }

    stopWoodyFrameCycle(state);

    state.tweens.forEach((tween) => {
      if (tween && tween.kill) {
        tween.kill();
      }
    });
    state.tweens.length = 0;
    state.accelTween = undefined;
    state.steadyTween = undefined;

    const rotors = root.querySelectorAll(ROTOR_SELECTOR);
    const frames = root.querySelectorAll(WOODY_FRAME_SELECTOR);
    if (typeof gsap !== "undefined") {
      gsap.killTweensOf(rotors);
      gsap.set(rotors, { clearProps: "transform" });
    }
    resetWoodyFrames(frames, 0);

    stateMap.delete(root);
  }

  function startRotorSpin(rotors, state) {
    if (!rotors.length || typeof gsap === "undefined") {
      return;
    }

    gsap.killTweensOf(rotors);
    gsap.set(rotors, { rotation: 0, transformOrigin: "50% 50%" });
    state.accelTween = undefined;
    state.steadyTween = undefined;
    state.tweens.length = 0;

    const accel = gsap.to(rotors, {
      rotation: 360,
      duration: ACCEL_DURATION,
      ease: ACCEL_EASE,
      overwrite: true,
      onComplete: () => {
        state.accelTween = undefined;
        const steady = gsap.to(rotors, {
          rotation: "+=360",
          duration: STEADY_DURATION,
          ease: "none",
          repeat: -1,
          overwrite: true,
        });
        state.steadyTween = steady;
        state.tweens.push(steady);

        if (!state.buzzInView || document.hidden) {
          steady.pause();
        }
      },
    });

    state.accelTween = accel;
    state.tweens.push(accel);
    state.rotorStarted = true;

    if (!state.buzzInView || document.hidden) {
      accel.pause();
    }
  }

  function initRoot(root) {
    if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
      return;
    }

    const buzz = root.querySelector(BUZZ_SELECTOR);
    const rotors = Array.from(root.querySelectorAll(ROTOR_SELECTOR));
    const woody = root.querySelector(WOODY_SELECTOR);
    const woodyFrames = Array.from(root.querySelectorAll(WOODY_FRAME_SELECTOR));

    if (!buzz || !rotors.length) {
      return;
    }

    destroyRoot(root);
    gsap.registerPlugin(ScrollTrigger);
    bindVisibilityListener();

    const state = {
      tweens: [],
      rotors,
      woodyFrames,
      buzzInView: false,
      woodyInView: false,
      rotorStarted: false,
      woodyStep: 0,
    };
    stateMap.set(root, state);
    activeRoots.add(root);

    if (prefersReducedMotion()) {
      gsap.set(rotors, { rotation: 0, clearProps: "transform" });
      resetWoodyFrames(woodyFrames, 0);
      return;
    }

    gsap.set(rotors, { rotation: 0, transformOrigin: "50% 50%" });

    state.trigger = ScrollTrigger.create({
      trigger: buzz,
      start: "top 75%",
      end: "bottom 25%",
      onEnter: () => {
        state.buzzInView = true;
        if (document.hidden) {
          return;
        }
        if (!state.rotorStarted) {
          startRotorSpin(rotors, state);
        } else {
          resumeRotor(state, rotors);
        }
      },
      onEnterBack: () => {
        state.buzzInView = true;
        if (!document.hidden) {
          resumeRotor(state, rotors);
        }
      },
      onLeave: () => {
        state.buzzInView = false;
        pauseRotor(state);
      },
      onLeaveBack: () => {
        state.buzzInView = false;
        pauseRotor(state);
      },
    });

    if (woody && woodyFrames.length >= 2) {
      state.woodyTrigger = ScrollTrigger.create({
        trigger: woody,
        start: "top 78%",
        end: "bottom 25%",
        onEnter: () => {
          state.woodyInView = true;
          if (!document.hidden) {
            startWoodyFrameCycle(woodyFrames, state);
          }
        },
        onEnterBack: () => {
          state.woodyInView = true;
          if (!document.hidden) {
            startWoodyFrameCycle(woodyFrames, state);
          }
        },
        onLeave: () => {
          state.woodyInView = false;
          stopWoodyFrameCycle(state);
        },
        onLeaveBack: () => {
          state.woodyInView = false;
          stopWoodyFrameCycle(state);
        },
      });
    }

    refreshTriggers(false);
  }

  function initAll(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => initRoot(root));
  }

  function onPageHide() {
    activeRoots.forEach((root) => destroyRoot(root));
    window.removeEventListener("pagehide", onPageHide);
  }

  function bootstrap() {
    const run = () => {
      initAll();
      refreshTriggers(true);
    };

    if (document.readyState === "complete") {
      run();
    } else {
      window.addEventListener("load", run, { once: true });
    }

    window.addEventListener("pagehide", onPageHide);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrap, { once: true });
  } else {
    bootstrap();
  }

  document.addEventListener("shopify:section:load", (event) => {
    const root = event.target.querySelector(ROOT_SELECTOR);
    if (!root) {
      return;
    }

    initRoot(root);
    refreshTriggers(true);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = event.target.querySelector(ROOT_SELECTOR);
    if (root) {
      destroyRoot(root);
    }
  });
})();
